import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    CalendarBlank, CaretLeft, CaretRight, Plus, X, CircleNotch, Trash,
    HardHat, MapPin, ListChecks, Flag, Clock,
} from "@phosphor-icons/react";

const TYPE_CFG = {
    site: { label: "On site", color: "var(--rom-orange)", Icon: HardHat },
    site_visit: { label: "Site visit", color: "#7c5cff", Icon: MapPin },
    task: { label: "Task", color: "var(--rom-navy)", Icon: ListChecks },
    action: { label: "Action", color: "#0891b2", Icon: Flag },
};
const TYPES = ["site", "site_visit", "task", "action"];

const iso = (d) => d.toISOString().slice(0, 10);
const SITE_HOURS = 8;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function monthGrid(year, month) {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const start = new Date(year, month, 1 - startOffset);
    const days = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
    }
    return days;
}

export default function ScheduleCalendar() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
    const [entries, setEntries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ project_id: "", employee_id: "", type: "" });
    const [modal, setModal] = useState(null); // {entry} or {date}

    const grid = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { date_from: iso(grid[0]), date_to: iso(grid[41]) };
        Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
        try {
            const { data } = await endpoints.schedules(params);
            setEntries(data || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    }, [grid, filter]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        endpoints.projects().then((r) => setProjects(r.data || [])).catch(() => {});
        endpoints.employees({ active_only: true }).then((r) => setEmployees(r.data || [])).catch(() => {});
    }, []);

    const byDay = useMemo(() => {
        const m = {};
        for (const e of entries) {
            const start = e.date, end = e.end_date || e.date;
            let d = new Date(start + "T00:00:00");
            const last = new Date(end + "T00:00:00");
            let guard = 0;
            while (d <= last && guard < 60) {
                const key = iso(d);
                (m[key] = m[key] || []).push(e);
                d.setDate(d.getDate() + 1);
                guard++;
            }
        }
        return m;
    }, [entries]);

    const todayKey = iso(new Date());
    function shift(delta) { setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; }); }

    return (
        <div className="px-6 py-8 space-y-5 max-w-[1500px] mx-auto" data-testid="schedule-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Schedules</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <CalendarBlank size={28} weight="bold" className="text-[var(--rom-orange)]" /> Global Calendar
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Who's on site, site visits, tasks & actions across every project.</p>
                </div>
                <button onClick={() => setModal({ date: todayKey })} className="btn btn-primary" data-testid="add-schedule-btn"><Plus size={14} weight="bold" /> New entry</button>
            </header>

            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button onClick={() => shift(-1)} className="btn btn-ghost btn-sm" data-testid="cal-prev"><CaretLeft size={14} weight="bold" /></button>
                    <div className="font-display font-bold text-lg w-44 text-center" data-testid="cal-title">{MONTHS[cursor.m]} {cursor.y}</div>
                    <button onClick={() => shift(1)} className="btn btn-ghost btn-sm" data-testid="cal-next"><CaretRight size={14} weight="bold" /></button>
                    <button onClick={() => { const n = new Date(); setCursor({ y: n.getFullYear(), m: n.getMonth() }); }} className="btn btn-ghost btn-sm">Today</button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select data-testid="cal-filter-project" value={filter.project_id} onChange={(e) => setFilter((f) => ({ ...f, project_id: e.target.value }))} className={filterCls}>
                        <option value="">All projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <select data-testid="cal-filter-employee" value={filter.employee_id} onChange={(e) => setFilter((f) => ({ ...f, employee_id: e.target.value }))} className={filterCls}>
                        <option value="">All people</option>
                        {employees.map((p) => <option key={p.id} value={p.id}>{p.employee_name}</option>)}
                    </select>
                    <select data-testid="cal-filter-type" value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))} className={filterCls}>
                        <option value="">All types</option>
                        {TYPES.map((t) => <option key={t} value={t}>{TYPE_CFG[t].label}</option>)}
                    </select>
                </div>
            </div>

            {/* Legend */}
            <div className="flex gap-4 flex-wrap text-xs">
                {TYPES.map((t) => { const c = TYPE_CFG[t]; return (
                    <span key={t} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: c.color }} /> {c.label}</span>
                ); })}
            </div>

            {/* Grid */}
            <div className="rom-card overflow-hidden">
                <div className="grid grid-cols-7 border-b border-[var(--rom-border)] bg-slate-50">
                    {DOW.map((d) => <div key={d} className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-3)] text-center">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {grid.map((d, i) => {
                        const key = iso(d);
                        const inMonth = d.getMonth() === cursor.m;
                        const items = byDay[key] || [];
                        const isToday = key === todayKey;
                        return (
                            <div key={i} data-testid={`cal-day-${key}`}
                                onClick={() => setModal({ date: key })}
                                className={`min-h-[104px] border-b border-r border-[var(--rom-border)] p-1.5 cursor-pointer hover:bg-slate-50 transition-colors ${inMonth ? "" : "bg-slate-50/50"}`}>
                                <div className={`text-[11px] font-semibold mb-1 ${isToday ? "w-5 h-5 rounded-full bg-[var(--rom-orange)] text-white flex items-center justify-center" : inMonth ? "text-[var(--rom-text)]" : "text-[var(--rom-text-3)]"}`}>{d.getDate()}</div>
                                <div className="space-y-1">
                                    {items.slice(0, 3).map((e) => { const c = TYPE_CFG[e.type] || TYPE_CFG.task; return (
                                        <div key={e.id + key} onClick={(ev) => { ev.stopPropagation(); setModal({ entry: e }); }}
                                            data-testid={`cal-entry-${e.id}`}
                                            className="text-[10px] leading-tight px-1.5 py-1 rounded text-white truncate" style={{ background: c.color }}
                                            title={`${c.label}: ${e.title}${e.employee_name ? " · " + e.employee_name : ""}`}>
                                            {e.employee_name ? `${e.employee_name.split(" ")[0]} · ` : ""}{e.title}
                                        </div>
                                    ); })}
                                    {items.length > 3 && <div className="text-[10px] text-[var(--rom-text-3)] px-1">+{items.length - 3} more</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {modal && (
                <ScheduleModal
                    initial={modal.entry || { date: modal.date }}
                    projects={projects} employees={employees}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                />
            )}
        </div>
    );
}

const filterCls = "h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs focus:border-[var(--rom-orange)] focus:outline-none bg-white max-w-[160px]";

function ScheduleModal({ initial, projects, employees, onClose, onSaved }) {
    const isEdit = !!initial.id;
    const [form, setForm] = useState({
        type: initial.type || "task", title: initial.title || "",
        project_id: initial.project_id || "", employee_id: initial.employee_id || "",
        date: initial.date || "", end_date: initial.end_date || initial.date || "",
        status: initial.status || "scheduled", notes: initial.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        if (!form.date) { alert("Date required"); return; }
        setBusy(true);
        const payload = { ...form };
        const proj = projects.find((p) => p.id === form.project_id);
        const emp = employees.find((e) => e.id === form.employee_id);
        payload.project_name = proj?.name || "";
        payload.employee_name = emp?.employee_name || "";
        try {
            let res;
            if (isEdit) res = await endpoints.updateSchedule(initial.id, payload);
            else res = await endpoints.createSchedule(payload);
            const drafted = res?.data?._timesheets_drafted || 0;
            if (drafted > 0) {
                toast.success(`${drafted} pending timesheet${drafted > 1 ? "s" : ""} auto-drafted for ${payload.employee_name} — crew just confirms hours.`);
            }
            onSaved();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function remove() {
        if (!window.confirm("Delete this entry?")) return;
        setBusy(true);
        try { await endpoints.deleteSchedule(initial.id); onSaved(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none";
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
            <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-lg">{isEdit ? "Edit entry" : "New schedule entry"}</h3>
                    <button onClick={onClose} className="p-1 text-[var(--rom-text-2)]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {TYPES.map((t) => { const c = TYPE_CFG[t]; return (
                            <button key={t} data-testid={`sched-type-${t}`} onClick={() => setF("type", t)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-xs font-semibold border transition-colors ${form.type === t ? "text-white" : "text-[var(--rom-text-2)] bg-white"}`}
                                style={form.type === t ? { background: c.color, borderColor: c.color } : { borderColor: "var(--rom-border)" }}>
                                <c.Icon size={13} weight="bold" /> {c.label}
                            </button>
                        ); })}
                    </div>
                    <div><label className={labelCls}>Title</label><input data-testid="sched-title" value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="e.g. Drying check, adjuster meet…" className={inputCls} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Date *</label><input data-testid="sched-date" type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} className={inputCls} /></div>
                        <div><label className={labelCls}>End date</label><input data-testid="sched-end" type="date" value={form.end_date} onChange={(e) => setF("end_date", e.target.value)} className={inputCls} /></div>
                        <div><label className={labelCls}>Project</label>
                            <select data-testid="sched-project" value={form.project_id} onChange={(e) => setF("project_id", e.target.value)} className={inputCls}>
                                <option value="">—</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div><label className={labelCls}>Assign to</label>
                            <select data-testid="sched-employee" value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)} className={inputCls}>
                                <option value="">—</option>
                                {employees.map((p) => <option key={p.id} value={p.id}>{p.employee_name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div><label className={labelCls}>Notes</label><input data-testid="sched-notes" value={form.notes} onChange={(e) => setF("notes", e.target.value)} className={inputCls} /></div>
                    {form.type === "site" && form.employee_id && form.project_id && (
                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-[6px] bg-[var(--rom-orange-soft)]/40 border border-[var(--rom-orange)]/30 text-xs" data-testid="autodraft-hint">
                            <Clock size={14} weight="bold" className="text-[var(--rom-orange-dark)] shrink-0 mt-0.5" />
                            <span>Pending timesheets ({SITE_HOURS}h/day) will be auto-drafted for each scheduled day — the crew just confirms hours, then it flows into the project P&amp;L.</span>
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-[var(--rom-border)] flex justify-between gap-2">
                    {isEdit ? <button onClick={remove} disabled={busy} className="btn btn-ghost text-red-600" data-testid="sched-delete"><Trash size={14} /> Delete</button> : <span />}
                    <div className="flex gap-2">
                        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="sched-save">{busy ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const labelCls = "block text-xs font-semibold text-[var(--rom-text-2)] mb-1";
